export interface ShippingDetail {
  TrackingNo: string | null;
  Svce: string | null;
  Ref1: string | null;
  Ref2: string | null;
  Ref3: string | null;
  Ref4: string | null;
  Ref5: string | null;
  ShippedDate: string | null;
}

export interface ShipRecord {
  InvoiceNo?: number;
  SDate: string;
  Name: string;
  Company?: string | null;
  Address1?: string | null;
  Address2?: string | null;
  City?: string | null;
  State?: string | null;
  ZipCode?: string | null;
  Country?: string | null;
  email?: string | null;
  PhoneNumber?: string | null;
  PO?: string | null;
  Carrier?: string | null;
  Priority?: string | null;
  ShippingCharges?: string | null;
  UPSAccount?: string | null;
  InsuranceAmt?: number | null;
  ShipmentContains?: string | null;
  Notes?: string | null;
  Department?: string | null;
  SentBy?: string | null;
  Email2?: string | null;
  ConfirmedDate?: string | null;
  ShippingDetails?: ShippingDetail[] | null;
}

function fmt(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function field(label: string, value: string | number | null | undefined) {
  if (value == null || value === '') return '';
  return `<div class="field"><div class="label">${label}</div><div class="value">${value}</div></div>`;
}

export function buildPrintHtml(p: ShipRecord, barcodeSvg = ''): string {
  const addr = [
    p.Address1,
    p.Address2,
    p.City && p.State ? `${p.City}, ${p.State}` : (p.City || p.State),
    p.ZipCode,
    p.Country,
  ].filter(Boolean).join('\n');

  const insurance = p.InsuranceAmt && p.InsuranceAmt > 0
    ? `$${p.InsuranceAmt.toFixed(2)}`
    : null;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Ship Request${p.InvoiceNo ? ` #${p.InvoiceNo}` : ''} — ${p.Name}</title>
  <style>
    @media print { @page { margin: 0.5in; } }
    body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 12px; color: #111827; margin: 0; }
    .doc-title { font-size: 22px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #111827; padding: 16px 16px 10px; }
    .header { background: #17479e; color: white; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
    .header h1 { margin: 0; font-size: 16px; font-weight: 700; }
    .header p  { margin: 0; font-size: 10px; opacity: 0.8; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
    .section { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
    .section h2 { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; margin: 0 0 8px 0; }
    .field { margin-bottom: 6px; }
    .field .label { font-size: 9px; font-weight: 600; text-transform: uppercase; color: #9ca3af; }
    .field .value { font-size: 12px; color: #111827; white-space: pre-line; }
    .notes { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin-top: 4px; }
    .confirmed { display:inline-block; padding:2px 8px; border-radius:99px; font-size:10px; font-weight:600; background:#d1fae5; color:#065f46; }
    .barcode-wrap { display: flex; justify-content: center; margin-top: 20px; padding-top: 16px; border-top: 1px solid #e5e7eb; }
    .barcode-wrap svg { display: block; }
  </style>
</head>
<body>
  <div class="doc-title">Request to Ship</div>
  <div class="header">
    <div>
      <h1>${p.InvoiceNo ? `#${p.InvoiceNo} · ` : ''}${p.Name}${p.Company ? ` · ${p.Company}` : ''}</h1>
      <p>Star Micronics America · Submitted ${fmt(p.SDate)}</p>
    </div>
    <div style="text-align:right">
      <span class="confirmed">CONFIRMED</span>
      ${p.SentBy || p.Department ? `<p style="margin:4px 0 0;font-size:10px;opacity:0.8">${[p.SentBy, p.Department].filter(Boolean).join(' · ')}</p>` : ''}
    </div>
  </div>

  <div class="grid">
    <div class="section">
      <h2>Ship To</h2>
      ${field('Name', p.Name)}
      ${field('Company', p.Company)}
      ${field('Address', addr || null)}
      ${field('Email', p.email)}
      ${field('Phone', p.PhoneNumber)}
    </div>
    <div class="section">
      <h2>Shipping Details</h2>
      ${field('Carrier', p.Carrier)}
      ${field('Priority', p.Priority)}
      ${field('Charges', p.ShippingCharges)}
      ${field('Account #', p.UPSAccount)}
      ${field('PO #', p.PO)}
      ${field('Contains', p.ShipmentContains)}
      ${field('Insurance', insurance)}
    </div>
  </div>

  ${p.Notes ? `<div class="notes"><h2 style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#6b7280;margin:0 0 6px 0">Notes</h2><div style="font-size:12px;color:#111827;white-space:pre-line">${p.Notes}</div></div>` : ''}
  ${barcodeSvg ? `<div class="barcode-wrap">${barcodeSvg}</div>` : ''}
</body>
</html>`;
}

export function openPrintWindow(record: ShipRecord) {
  import('jsbarcode').then(({ default: JsBarcode }) => {
    let barcodeSvg = '';
    if (record.InvoiceNo) {
      const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      JsBarcode(svgEl, String(record.InvoiceNo), {
        format: 'CODE128',
        width: 2,
        height: 60,
        displayValue: true,
        fontSize: 14,
        margin: 8,
      });
      barcodeSvg = svgEl.outerHTML;
    }

    const win = window.open('', '_blank', 'width=820,height=700');
    if (!win) return;
    win.document.write(buildPrintHtml(record, barcodeSvg));
    win.document.close();
    win.focus();
    win.print();
    win.close();
  });
}
